// ═══════════════════════════════════════════════════════════════
// TransitionWorkspace — Modal articulation inter-chapitres (S58 P2)
// ═══════════════════════════════════════════════════════════════
//
// Layout split haut/bas :
//   - Haut : en-tête + extraits (fin N, début N+1)
//   - Bas  : diagnostic IA + proposition éditable + boutons apply
//
// Modal overlay plein cadre, backdrop blur, Escape pour fermer.
// Sélecteur modèle Sonnet 4.6 (défaut) / Opus 4.7 (premium).
// Propositions éditables avant application.
// Application non-destructive (versioning forcé côté backend).
// ═══════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { X, ArrowLeftRight, Loader, Sparkles, Wand2, AlertCircle } from 'lucide-react';
import { analyzeTransition, applyTransition, getChapter } from '../../services/bookApi';

const TIER_LABEL = { sonnet: 'Sonnet 4.6', opus: 'Opus 4.7' };

const TransitionWorkspace = ({
    bookId,
    fromChapter,
    toChapter,
    onClose,
    onApplied,
    getCachedResult,
    putCachedResult,
}) => {
    const [modelTier, setModelTier] = useState('sonnet');
    const [analyzing, setAnalyzing] = useState(false);
    const [applying, setApplying] = useState(false);
    const [result, setResult] = useState(null);
    const [closingDraft, setClosingDraft] = useState('');
    const [openingDraft, setOpeningDraft] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(null);
    const [fromFull, setFromFull] = useState(null);
    const [toFull, setToFull] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const initRef = useRef(false);

    // Hit cache au mount pour le tier par défaut (sonnet)
    useEffect(() => {
        if (initRef.current) return;
        if (!fromChapter?.id || !toChapter?.id) return;
        initRef.current = true;
        if (getCachedResult) {
            const cached = getCachedResult(fromChapter.id, toChapter.id, modelTier);
            if (cached) {
                setResult(cached);
                setClosingDraft(cached?.proposal?.closing_addition || '');
                setOpeningDraft(cached?.proposal?.opening_modification || '');
                setFromCache(true);
            }
        }
    }, [fromChapter?.id, toChapter?.id, modelTier, getCachedResult]);

    // Hydratation async content_text si manquant (pattern S57-G fullCurrentChapter)
    useEffect(() => {
        let cancelled = false;
        if (!bookId) return;
        if (fromChapter?.id && fromChapter.content_text == null) {
            getChapter(bookId, fromChapter.id).then(r => {
                if (!cancelled && r?.chapter) setFromFull(r.chapter);
            }).catch(() => {});
        } else if (fromChapter?.id) {
            setFromFull(fromChapter);
        }
        if (toChapter?.id && toChapter.content_text == null) {
            getChapter(bookId, toChapter.id).then(r => {
                if (!cancelled && r?.chapter) setToFull(r.chapter);
            }).catch(() => {});
        } else if (toChapter?.id) {
            setToFull(toChapter);
        }
        return () => { cancelled = true; };
    }, [bookId, fromChapter?.id, toChapter?.id]);

    // Escape pour fermer (sauf si apply en cours)
    useEffect(() => {
        const h = (e) => {
            if (e.key === 'Escape' && !applying) onClose?.();
        };
        document.addEventListener('keydown', h);
        return () => document.removeEventListener('keydown', h);
    }, [applying, onClose]);

    // UX S58 — pas d'auto-analyse au mount (coût LLM).
    // L'utilisateur clique explicitement « Lancer l'analyse » pour déclencher.
    // Évite les appels accidentels à chaque ouverture de la modal.

    const runAnalyze = async (tier) => {
        setAnalyzing(true);
        setError('');
        setResult(null);
        setClosingDraft('');
        setOpeningDraft('');
        setSuccess(null);
        setFromCache(false);
        try {
            const res = await analyzeTransition(bookId, fromChapter.id, toChapter.id, { modelTier: tier });
            setResult(res);
            setClosingDraft(res?.proposal?.closing_addition || '');
            setOpeningDraft(res?.proposal?.opening_modification || '');
            // Mise en cache pour éviter de repayer si l'utilisateur rouvre la modal
            if (putCachedResult) {
                putCachedResult(fromChapter.id, toChapter.id, tier, res);
            }
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur analyse transition');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleChangeTier = (tier) => {
        if (tier === modelTier || analyzing || applying) return;
        setModelTier(tier);
        // S58 : check cache pour ce tier — si hit, charge direct sans LLM.
        // Sinon bandeau ambre signale qu'il faut cliquer Relancer.
        if (getCachedResult) {
            const cached = getCachedResult(fromChapter.id, toChapter.id, tier);
            if (cached) {
                setResult(cached);
                setClosingDraft(cached?.proposal?.closing_addition || '');
                setOpeningDraft(cached?.proposal?.opening_modification || '');
                setFromCache(true);
                setError('');
                setSuccess(null);
            }
        }
    };

    const handleApply = async ({ applyClosing, applyOpening }) => {
        if (!applyClosing && !applyOpening) return;
        setApplying(true);
        setError('');
        try {
            const res = await applyTransition(bookId, fromChapter.id, toChapter.id, {
                closingAddition: applyClosing ? closingDraft.trim() : null,
                openingModification: applyOpening ? openingDraft.trim() : null,
            });
            setSuccess(res);
            if (onApplied) onApplied(res);
        } catch (err) {
            setError(err.detail?.message || err.message || 'Erreur application');
        } finally {
            setApplying(false);
        }
    };

    const hasClosing = !!(closingDraft && closingDraft.trim().length > 0);
    const hasOpening = !!(openingDraft && openingDraft.trim().length > 0);

    return (
        <div
            style={{
                position: 'fixed', inset: 0, zIndex: 120,
                background: 'rgba(0,0,0,0.65)',
                backdropFilter: 'blur(4px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20,
            }}
            onClick={() => { if (!applying) onClose?.(); }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: 820, maxWidth: '96vw', maxHeight: '92vh',
                    display: 'flex', flexDirection: 'column',
                    background: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: 14,
                    boxShadow: '0 30px 80px rgba(0,0,0,0.55)',
                    overflow: 'hidden',
                }}
            >
                {/* HEADER */}
                <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 22px',
                    borderBottom: '1px solid var(--card-border)',
                    background: 'linear-gradient(180deg, rgba(139,92,246,0.08), transparent)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <div style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                        }}>
                            <ArrowLeftRight size={16} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                                Transition — chapitre {fromChapter?.position} → {toChapter?.position}
                            </div>
                            <div style={{
                                fontSize: 11, color: 'var(--text-secondary)', marginTop: 2,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {fromChapter?.title} → {toChapter?.title}
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                        {/* Sélecteur modèle */}
                        <div style={{
                            display: 'flex', gap: 0, borderRadius: 8,
                            background: 'rgba(255,255,255,0.04)',
                            border: '1px solid var(--card-border)',
                            overflow: 'hidden',
                        }}>
                            {['sonnet', 'opus'].map(tier => (
                                <button
                                    key={tier}
                                    onClick={() => handleChangeTier(tier)}
                                    disabled={analyzing || applying}
                                    style={{
                                        padding: '5px 12px', fontSize: 11, fontWeight: 500,
                                        background: modelTier === tier ? 'rgba(139,92,246,0.2)' : 'transparent',
                                        border: 'none',
                                        color: modelTier === tier ? '#c4b5fd' : 'var(--text-secondary)',
                                        cursor: (analyzing || applying) ? 'not-allowed' : 'pointer',
                                        borderRight: tier === 'sonnet' ? '1px solid var(--card-border)' : 'none',
                                    }}
                                >
                                    {TIER_LABEL[tier]}
                                </button>
                            ))}
                        </div>
                        <button
                            onClick={onClose}
                            disabled={applying}
                            style={{
                                background: 'transparent', border: 'none',
                                color: 'var(--text-secondary)',
                                cursor: applying ? 'not-allowed' : 'pointer',
                                padding: 4,
                            }}
                            title="Fermer (Échap)"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* CORPS scrollable */}
                <div style={{ flex: 1, overflow: 'auto', padding: '18px 22px' }}>
                    {/* Extraits haut (fin N / début N+1) */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12,
                        marginBottom: 18,
                    }}>
                        <ExtractPane
                            label={`Fin du chapitre ${fromChapter?.position}`}
                            text={extractEnd(fromFull?.content_text ?? fromChapter?.content_text)}
                            beat={fromFull?.closing_beat ?? fromChapter?.closing_beat}
                            beatLabel="Beat de fin"
                        />
                        <ExtractPane
                            label={`Début du chapitre ${toChapter?.position}`}
                            text={extractStart(toFull?.content_text ?? toChapter?.content_text)}
                            beat={toFull?.opening_hook ?? toChapter?.opening_hook}
                            beatLabel="Incipit retenu"
                        />
                    </div>

                    {/* Écran d'accueil — avant toute analyse */}
                    {!result && !analyzing && !error && (
                        <div style={{
                            padding: '36px 24px', borderRadius: 12,
                            background: 'rgba(139,92,246,0.04)',
                            border: '1px dashed rgba(139,92,246,0.25)',
                            textAlign: 'center',
                        }}>
                            <div style={{
                                width: 48, height: 48, borderRadius: 12, margin: '0 auto 12px',
                                background: 'rgba(139,92,246,0.15)', color: '#a78bfa',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <ArrowLeftRight size={22} />
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>
                                Analyser la jointure
                            </div>
                            <div style={{
                                fontSize: 12, color: 'var(--text-secondary)', marginBottom: 18,
                                lineHeight: 1.5, maxWidth: 520, margin: '0 auto 18px',
                            }}>
                                L'IA lit la fin du chapitre {fromChapter?.position} et le début du chapitre {toChapter?.position}, diagnostique la jointure, et propose des patchs éditables. Rien n'est appliqué sans ton clic.
                            </div>
                            <button
                                onClick={() => runAnalyze(modelTier)}
                                style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    padding: '10px 22px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    background: 'linear-gradient(180deg, #8b5cf6, #7c3aed)',
                                    border: '1px solid #6d28d9',
                                    color: '#ffffff', cursor: 'pointer',
                                    boxShadow: '0 6px 20px rgba(139,92,246,0.35)',
                                }}
                            >
                                <Sparkles size={14} />
                                Lancer l'analyse ({TIER_LABEL[modelTier]})
                            </button>
                            <div style={{ fontSize: 10.5, color: 'var(--text-muted)', marginTop: 10 }}>
                                Coût estimé : {modelTier === 'sonnet' ? '≈ $0.02' : '≈ $0.20 (premium)'} · durée {modelTier === 'sonnet' ? '~6s' : '~20s'}
                            </div>
                        </div>
                    )}

                    {/* Analyse en cours */}
                    {analyzing && (
                        <div style={{
                            padding: '24px 20px', borderRadius: 10,
                            background: 'rgba(139,92,246,0.06)',
                            border: '1px solid rgba(139,92,246,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            color: '#c4b5fd', fontSize: 13,
                        }}>
                            <Loader size={14} className="animate-spin" />
                            Analyse de la jointure… ({TIER_LABEL[modelTier]})
                        </div>
                    )}

                    {/* Erreur */}
                    {error && !analyzing && (
                        <div style={{
                            padding: '12px 14px', borderRadius: 8, marginBottom: 12,
                            background: 'rgba(239,68,68,0.08)',
                            border: '1px solid rgba(239,68,68,0.25)',
                            color: '#ef4444', fontSize: 12,
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                        }}>
                            <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                            <div>{error}</div>
                        </div>
                    )}

                    {/* Bandeau modèle changé — relance requise */}
                    {result && !analyzing && result.model_used && !result.model_used.toLowerCase().includes(modelTier) && (
                        <div style={{
                            padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                            background: 'rgba(245,158,11,0.08)',
                            border: '1px solid rgba(245,158,11,0.3)',
                            color: '#f59e0b', fontSize: 12,
                            display: 'flex', alignItems: 'center', gap: 8,
                        }}>
                            <Sparkles size={13} />
                            <span>
                                Modèle changé pour <strong>{TIER_LABEL[modelTier]}</strong> — l'analyse affichée vient encore de {result.model_used}. Clique <strong>Relancer l'analyse</strong> pour recalculer.
                            </span>
                        </div>
                    )}

                    {/* Résultat */}
                    {result && !analyzing && (
                        <>
                            {/* Diagnostic */}
                            <Section title="Diagnostic IA">
                                <div style={{
                                    padding: '14px 16px', borderRadius: 8,
                                    background: 'rgba(255,255,255,0.03)',
                                    border: '1px solid var(--card-border)',
                                    fontSize: 13, lineHeight: 1.65,
                                    color: 'var(--text)',
                                    fontFamily: 'Georgia, "Times New Roman", serif',
                                    whiteSpace: 'pre-wrap',
                                }}>
                                    {result.diagnostic}
                                </div>
                            </Section>

                            {/* Proposition closing */}
                            <Section title={`Ajout en fin de chapitre ${fromChapter?.position}`}>
                                {closingDraft ? (
                                    <textarea
                                        value={closingDraft}
                                        onChange={(e) => setClosingDraft(e.target.value)}
                                        rows={5}
                                        style={textareaStyle}
                                        placeholder="(aucune proposition de clôture)"
                                    />
                                ) : (
                                    <EmptyNote>Aucune proposition de clôture — l'IA estime que la fin actuelle est suffisante.</EmptyNote>
                                )}
                            </Section>

                            {/* Proposition opening */}
                            <Section title={`Nouveau premier paragraphe du chapitre ${toChapter?.position}`}>
                                {openingDraft ? (
                                    <textarea
                                        value={openingDraft}
                                        onChange={(e) => setOpeningDraft(e.target.value)}
                                        rows={5}
                                        style={textareaStyle}
                                        placeholder="(aucune modification d'ouverture)"
                                    />
                                ) : (
                                    <EmptyNote>Aucune modification d'ouverture — l'IA estime que le début actuel est naturel.</EmptyNote>
                                )}
                            </Section>

                            {/* Notes stylistiques */}
                            {(result.style_notes?.chapter_n || result.style_notes?.chapter_m) && (
                                <Section title="Notes stylistiques">
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                        {result.style_notes?.chapter_n && (
                                            <StyleNote label={`Chap. ${fromChapter?.position}`} content={result.style_notes.chapter_n} />
                                        )}
                                        {result.style_notes?.chapter_m && (
                                            <StyleNote label={`Chap. ${toChapter?.position}`} content={result.style_notes.chapter_m} />
                                        )}
                                    </div>
                                </Section>
                            )}

                            {/* Meta coût + modèle + indicateur cache */}
                            <div style={{
                                marginTop: 10, fontSize: 10.5, color: 'var(--text-muted)',
                                display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
                            }}>
                                <span>{result.model_used}</span>
                                {result.duration_ms != null && <span>{(result.duration_ms/1000).toFixed(1)}s</span>}
                                {result.cost_usd != null && <span>${Number(result.cost_usd).toFixed(4)}</span>}
                                {fromCache && (
                                    <span style={{
                                        padding: '2px 8px', borderRadius: 10,
                                        background: 'rgba(16,185,129,0.12)',
                                        color: '#10b981', fontSize: 10, fontWeight: 600,
                                    }}>
                                        ✓ Depuis le cache — pas de nouvelle dépense
                                    </span>
                                )}
                            </div>
                        </>
                    )}

                    {success && (
                        <div style={{
                            marginTop: 16, padding: '12px 14px', borderRadius: 8,
                            background: 'rgba(16,185,129,0.08)',
                            border: '1px solid rgba(16,185,129,0.3)',
                            color: '#10b981', fontSize: 12, lineHeight: 1.6,
                        }}>
                            ✓ Transition appliquée.
                            {success.from_version_number && <> Chapitre {fromChapter?.position} → v{success.from_version_number}.</>}
                            {success.to_version_number && <> Chapitre {toChapter?.position} → v{success.to_version_number}.</>}
                            <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 4 }}>
                                Les versions précédentes restent restaurables depuis l'historique.
                            </div>
                        </div>
                    )}
                </div>

                {/* FOOTER actions */}
                <div style={{
                    padding: '14px 22px',
                    borderTop: '1px solid var(--card-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 10, flexWrap: 'wrap',
                    background: 'var(--card-bg-secondary)',
                }}>
                    {/* Bouton Relancer visible seulement si un résultat existe */}
                    {result ? (
                        <button
                            onClick={() => runAnalyze(modelTier)}
                            disabled={analyzing || applying}
                            title={`Relancer l'analyse avec ${TIER_LABEL[modelTier]}`}
                            style={secondaryBtn(analyzing || applying)}
                        >
                            <Sparkles size={12} /> Relancer l'analyse
                        </button>
                    ) : <div />}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button
                            onClick={onClose}
                            disabled={applying}
                            style={secondaryBtn(applying)}
                        >
                            Fermer
                        </button>
                        <button
                            onClick={() => handleApply({ applyClosing: true, applyOpening: false })}
                            disabled={!hasClosing || applying || analyzing}
                            style={primaryBtn(!hasClosing || applying || analyzing)}
                        >
                            <Wand2 size={12} /> Appliquer ajout fin
                        </button>
                        <button
                            onClick={() => handleApply({ applyClosing: false, applyOpening: true })}
                            disabled={!hasOpening || applying || analyzing}
                            style={primaryBtn(!hasOpening || applying || analyzing)}
                        >
                            <Wand2 size={12} /> Appliquer modif début
                        </button>
                        <button
                            onClick={() => handleApply({ applyClosing: true, applyOpening: true })}
                            disabled={(!hasClosing && !hasOpening) || applying || analyzing}
                            style={{
                                ...primaryBtn((!hasClosing && !hasOpening) || applying || analyzing),
                                background: 'linear-gradient(180deg, #8b5cf6, #7c3aed)',
                                border: '1px solid #6d28d9',
                                color: '#ffffff',
                                fontWeight: 600,
                            }}
                        >
                            {applying ? <Loader size={12} className="animate-spin" /> : <Wand2 size={12} />}
                            {applying ? 'Application…' : 'Appliquer les deux'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ─── Sous-composants ─────────────────────────────────────────────────

const ExtractPane = ({ label, text, beat, beatLabel }) => (
    <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid var(--card-border)',
    }}>
        <div style={{
            fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
        }}>
            {label}
        </div>
        <div style={{
            fontSize: 12, lineHeight: 1.55, color: 'var(--text-secondary)',
            fontFamily: 'Georgia, "Times New Roman", serif',
            whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto',
        }}>
            {text || <em style={{ color: 'var(--text-muted)' }}>(chapitre vide)</em>}
        </div>
        {beat && (
            <div style={{
                marginTop: 8, paddingTop: 8,
                borderTop: '1px dashed var(--card-border)',
                fontSize: 10.5, color: 'var(--text-muted)',
            }}>
                <strong style={{ color: 'var(--text-secondary)' }}>{beatLabel}</strong> · {beat}
            </div>
        )}
    </div>
);

const Section = ({ title, children }) => (
    <div style={{ marginBottom: 14 }}>
        <div style={{
            fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary)',
            textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6,
        }}>
            {title}
        </div>
        {children}
    </div>
);

const StyleNote = ({ label, content }) => (
    <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(139,92,246,0.05)',
        border: '1px solid rgba(139,92,246,0.15)',
    }}>
        <div style={{ fontSize: 10.5, fontWeight: 600, color: '#a78bfa', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--text)' }}>{content}</div>
    </div>
);

const EmptyNote = ({ children }) => (
    <div style={{
        padding: '10px 12px', borderRadius: 8,
        background: 'rgba(255,255,255,0.02)',
        border: '1px dashed var(--card-border)',
        color: 'var(--text-muted)', fontSize: 11.5, lineHeight: 1.5,
        fontStyle: 'italic',
    }}>
        {children}
    </div>
);

const textareaStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid var(--card-border)',
    color: 'var(--text)', fontSize: 13, lineHeight: 1.6,
    fontFamily: 'Georgia, "Times New Roman", serif',
    resize: 'vertical', minHeight: 100,
    boxSizing: 'border-box',
};

const primaryBtn = (disabled) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 500,
    background: 'rgba(139,92,246,0.18)',
    border: '1px solid rgba(139,92,246,0.35)',
    color: '#c4b5fd',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.45 : 1,
});

const secondaryBtn = (disabled) => ({
    display: 'flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', borderRadius: 6, fontSize: 12,
    background: 'transparent',
    border: '1px solid var(--card-border)',
    color: 'var(--text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
});

// ─── Helpers ──────────────────────────────────────────────────────────

function extractEnd(text, n = 500) {
    if (!text) return '';
    const s = String(text).trim();
    if (s.length <= n) return s;
    return '…' + s.substring(s.length - n).trim();
}

function extractStart(text, n = 500) {
    if (!text) return '';
    const s = String(text).trim();
    if (s.length <= n) return s;
    return s.substring(0, n).trim() + '…';
}

export default TransitionWorkspace;
